import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { getPhotoUrl } from '../utils/bucketUrl';

const TuningPointsModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: 20,
      transition: { duration: 0.2, ease: 'easeIn' },
    },
  };

  const benefitVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, delay: i * 0.08 },
    }),
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
          />

          {/* Modal */}
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden">
              {/* Header with Background Image */}
              <div className="relative h-48 md:h-56 overflow-hidden">
                <img
                  src={getPhotoUrl('IMG_8282.jpeg')}
                  alt="Tuning workshop"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-racing-black/60 to-transparent"></div>

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 bg-racing-red/80 hover:bg-racing-red text-white p-2 rounded-full transition-all duration-300 z-10"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Title Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <h2 className="text-3xl md:text-4xl font-display font-bold text-white">
                    {t('tuningPoints.modalTitle')}
                  </h2>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 max-h-[calc(100vh-240px)] md:max-h-[calc(100vh-280px)] overflow-y-auto">
                {/* Benefits Section */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <span className="w-1.5 h-1.5 bg-racing-red rounded-full mr-3"></span>
                    {t('tuningPoints.benefitsTitle')}
                  </h3>
                  <ul className="space-y-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <motion.li
                        key={i}
                        custom={i - 1}
                        variants={benefitVariants}
                        initial="hidden"
                        animate="visible"
                        className="flex items-start bg-gray-800/50 p-4 rounded-lg border border-gray-700/50 hover:border-racing-red/30 transition-colors duration-300"
                      >
                        <svg className="w-5 h-5 text-racing-red flex-shrink-0 mt-1 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-300 leading-relaxed">
                          {t(`tuningPoints.modalBenefit${i}`)}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Daily Tasks Section */}
                <div className="border-t border-gray-700 pt-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <span className="w-1.5 h-1.5 bg-racing-red rounded-full mr-3"></span>
                    {t('tuningPoints.modalDailyTitle')}
                  </h3>
                  <ul className="space-y-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <motion.li
                        key={i}
                        custom={i + 5}
                        variants={benefitVariants}
                        initial="hidden"
                        animate="visible"
                        className="flex items-start bg-gray-800/50 p-4 rounded-lg border border-gray-700/50 hover:border-racing-red/30 transition-colors duration-300"
                      >
                        <svg className="w-5 h-5 text-racing-red flex-shrink-0 mt-1 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-300 leading-relaxed">
                          {t(`tuningPoints.modalDaily${i}`)}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Close Button */}
                <div className="mt-8 pt-6 border-t border-gray-700">
                  <button
                    onClick={onClose}
                    className="w-full bg-racing-red hover:bg-racing-darkRed text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TuningPointsModal;
